import React from 'react';
import { Link } from 'react-router-dom';
import { canonicalFor, usePageMeta } from '../../hooks/usePageMeta';
import { PageHeader } from '../../components/page/PageHeader';
import { ContentSection } from '../../components/page/ContentSection';
import { SpecList } from '../../components/page/SpecList';
import { CautionNote } from '../../components/page/CautionNote';
import { TechAssistCallout } from '../../components/page/TechAssistCallout';
import { RelatedLinks } from '../../components/page/RelatedLinks';
const linkClass = 'text-navy underline decoration-copper underline-offset-4 hover:text-copper';
export function Aluminum() {
  usePageMeta({
    title: '6061-T6 Aluminum Starting Guidance | Robertson Precision',
    description: 'Published Shear Geometry starting guidance for 6061-T6 aluminum: APGX 331R/332R MG12 roughing at .025–.050 IPR and -30R chipbreakers at .010–.020 IPR.',
    canonical: canonicalFor('/applications/aluminum/')
  });
  return <>
      <PageHeader eyebrow="Applications" title="Turning 6061-T6 Aluminum" lead="The published guidance for 6061-T6 aluminum separates roughing with MG12 inserts from lighter-feed chip control with -30R chipbreaker inserts. These values apply to 6061-T6; they are not general guidance for other aluminum alloys." crumbs={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Applications',
      href: '/applications/'
    }, {
      label: 'Aluminum'
    }]} data-id="element-677" data-mp="15rrdcywbudzm" />

      <ContentSection id="roughing" title="Roughing — APGX 331R MG12 or APGX 332R MG12" intro={<p data-id="element-679" data-mp="d3va1bc95ntv">Recommended chip-control range on a 20 hp CNC lathe.</p>} data-id="element-678" data-mp="rrq691pgyabp">
        <SpecList ariaLabel="6061-T6 roughing starting guidance" specs={[{
        label: 'DOC per side',
        value: '.050–.300 in'
      }, {
        label: 'Min feed',
        value: '.025 IPR'
      }, {
        label: 'Max feed',
        value: '.050 IPR'
      }, {
        label: 'Machine',
        value: '20 hp CNC lathe'
      }]} data-id="element-680" data-mp="i26ltrm3y8eq" />
      </ContentSection>

      <ContentSection id="chipbreaker" tone="warm" title="Lighter feeds — -30R chipbreaker inserts" intro={<p data-id="element-682" data-mp="210bi1u3ezevf">
            APGX 331-30R or APGX 332-30R (Grade MG12) for lighter feed rates,
            where tighter chip control is the priority.
          </p>} data-id="element-681" data-mp="2arz75oci0jtq">
        <SpecList ariaLabel="6061-T6 chipbreaker starting guidance" specs={[{
        label: 'DOC per side',
        value: '.015–.300 in'
      }, {
        label: 'Feed',
        value: '.010–.020 IPR'
      }, {
        label: 'Insert',
        value: '-30R chipbreaker'
      }, {
        label: 'Material',
        value: '6061-T6'
      }]} data-id="element-683" data-mp="2tpifsqv1o0fm" />
      </ContentSection>

      <ContentSection id="reading" title="Reading these values" data-id="element-684" data-mp="2fphgvgycb32s">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-685" data-mp="18d8m99q4hi8v">
          <div className="rp-measure space-y-4 text-[15.5px] leading-[1.7] text-graphite/85" data-id="element-686" data-mp="14alrp122hj7z">
            <p data-id="element-687" data-mp="3fxxo06yh7nc1">
              <strong className="font-semibold text-navy" data-id="element-688" data-mp="3fyh6bos1gdql">Feed</strong> is in
              inches per revolution (IPR) — how far the tool advances for each
              spindle revolution.
            </p>
            <p data-id="element-689" data-mp="3oy3w1dbenv3t">
              <strong className="font-semibold text-navy" data-id="element-690" data-mp="3oxkomqjdsp41">Depth of cut</strong>{' '}
              is per side.
            </p>
            <p data-id="element-691" data-mp="3tqn8u829qm7k">
              <strong className="font-semibold text-navy" data-id="element-692" data-mp="3tr6spubhbko8">Speed:</strong> no
              numeric surface speed has been supplied for aluminum. Confirm
              spindle speed for your setup with the technical staff.
            </p>
          </div>
          <CautionNote data-id="element-693" data-mp="bksfjxl1beqw">
            <p data-id="element-694" data-mp="bk90comrr4gk">
              Machine horsepower, workholding and fixturing, part rigidity, tool
              overhang and machine limits govern the actual parameters. Reduce
              feed and depth of cut where the setup requires it.
            </p>
            <p data-id="element-695" data-mp="2i9njnlb7wnhr">
              The{' '}
              <Link to="/technical-resources/safety/" className={linkClass} data-id="element-696" data-mp="2iap4ghi1coco">
                workholding chart
              </Link>{' '}
              is a separate published reference for these roughing inserts, with
              some different values. Don’t combine the two, and don’t treat
              either as a promise that every combination is safe.
            </p>
          </CautionNote>
        </div>
      </ContentSection>

      <ContentSection id="help" tone="warm" data-id="element-697" data-mp="149irwvq1yq9l">
        <TechAssistCallout location="aluminum" data-id="element-698" data-mp="14akbxpp1qn3g" />
      </ContentSection>

      <RelatedLinks links={[{
      label: 'Inserts & Chipbreakers',
      href: '/products/inserts/',
      description: 'MG12 roughing and -30R chipbreakers.'
    }, {
      label: 'Workholding & Safety',
      href: '/technical-resources/safety/',
      description: 'Full workholding chart for 6061-T6.'
    }, {
      label: 'Starting Data',
      href: '/technical-resources/starting-data/',
      description: 'All materials in one table.'
    }, {
      label: 'Tool Selection',
      href: '/technical-resources/tool-selection/',
      description: 'Holder, insert and application.'
    }]} data-id="element-699" data-mp="zay1fo0lrkyc" />
    </>;
}